/**
 * Configuración de márgenes de ganancia por rango de precio
 * Los precios son en pesos argentinos (ARS)
 * 
 * Rangos:
 * - Baratos: < $10.000 → 50% de margen
 * - Medios: $10.000 - $50.000 → 35% de margen  
 * - Caros: > $50.000 → 30% de margen
 */

export const MARGIN_TIERS = [
  {
    name: "Baratos",
    minPrice: 0,
    maxPrice: 10000,
    margin: 0.50, // 50%
    color: "#22c55e", // verde
    emoji: "🟢",
  },
  {
    name: "Medios",
    minPrice: 10000,
    maxPrice: 50000,
    margin: 0.35, // 35%
    color: "#3b82f6", // azul
    emoji: "🔵",
  },
  {
    name: "Caros",
    minPrice: 50000,
    maxPrice: Infinity,
    margin: 0.30, // 30%
    color: "#ef4444", // rojo
    emoji: "🔴",
  },
];

/**
 * Obtiene el tier de margen según el precio de costo
 * @param {number} costPrice - Precio de costo del mayorista
 * @returns {object} - Tier con name, margin, color, emoji
 */
export const getMarginTier = (costPrice) => {
  const price = Number(costPrice) || 0;
  
  for (const tier of MARGIN_TIERS) {
    if (price >= tier.minPrice && price < tier.maxPrice) {
      return tier;
    }
  }
  
  // Fallback al tier de caros
  return MARGIN_TIERS[MARGIN_TIERS.length - 1];
};

/**
 * Calcula el precio de venta aplicando el margen correspondiente
 * @param {number} costPrice - Precio de costo del mayorista
 * @returns {object} - { costPrice, salePrice, margin, profit, tier }
 */
export const calculateSalePrice = (costPrice) => {
  const cost = Number(costPrice) || 0;
  const tier = getMarginTier(cost);
  
  const salePrice = Math.round(cost * (1 + tier.margin));
  const profit = salePrice - cost;
  
  return {
    costPrice: cost,
    salePrice,
    margin: tier.margin,
    marginPercent: Math.round(tier.margin * 100),
    profit,
    tier,
  };
};

/**
 * Calcula las estadísticas de ganancias para un array de productos
 * @param {array} products - Array de productos con costPrice, salePrice y stock
 * @returns {object} - Estadísticas de ganancias
 */
export const calculateProfitStats = (products) => {
  const stats = {
    totalProducts: 0,
    totalUnits: 0,
    totalCost: 0,
    totalSaleValue: 0,
    totalProfit: 0,
    averageMargin: 0,
    byTier: {},
  };
  
  // Inicializar tiers
  MARGIN_TIERS.forEach(tier => {
    stats.byTier[tier.name] = {
      ...tier,
      products: 0,
      units: 0,
      cost: 0,
      saleValue: 0,
      profit: 0,
    };
  });
  
  for (const product of products) {
    const stock = Number(product.stock) || 0;
    const costPrice = Number(product.costPrice || product.price) || 0;
    const salePrice = Number(product.salePrice || product.price) || 0;
    
    if (stock <= 0 || costPrice <= 0) continue;
    
    const tier = getMarginTier(costPrice);
    const productCost = costPrice * stock;
    const productSaleValue = salePrice * stock;
    const productProfit = productSaleValue - productCost;
    
    stats.totalProducts++;
    stats.totalUnits += stock;
    stats.totalCost += productCost;
    stats.totalSaleValue += productSaleValue;
    stats.totalProfit += productProfit;
    
    stats.byTier[tier.name].products++;
    stats.byTier[tier.name].units += stock;
    stats.byTier[tier.name].cost += productCost;
    stats.byTier[tier.name].saleValue += productSaleValue;
    stats.byTier[tier.name].profit += productProfit;
  }
  
  if (stats.totalCost > 0) {
    stats.averageMargin = ((stats.totalSaleValue - stats.totalCost) / stats.totalCost) * 100;
  }
  
  return stats;
};

const marginConfigDefault = {
  MARGIN_TIERS,
  getMarginTier,
  calculateSalePrice,
  calculateProfitStats,
};

export default marginConfigDefault;
